import { useAuth } from "@dl/web-core-js/auth";
import {
  columns as defineColumns,
  DataTable,
  type RowEditorConfig,
} from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ClientPricingRuleClient,
  NexpimClientClient,
  type ClientPricingRuleDTO,
} from "../../api/pricing";

/**
 * One row per active client, with that client's pricing rule (if any) joined
 * in flat so the inline editor can touch the three rule fields directly.
 * `rule_id === 0` means no rule exists yet — the first save creates one.
 */
interface Row {
  id: number;
  name: string;
  no: string;
  active: boolean;
  currency: string;
  discount: number;
  rule_id: number;
  has_shipping: boolean;
  margin_percent_adjustment: number;
  margin_amount_adjustment: number;
}

function toRule(row: Row): ClientPricingRuleDTO {
  return {
    id: row.rule_id,
    client_id: row.id,
    has_shipping: row.has_shipping,
    margin_percent_adjustment: row.margin_percent_adjustment,
    margin_amount_adjustment: row.margin_amount_adjustment,
  };
}

/**
 * Pricing → Client rules: per-client pricing adjustments — whether their
 * target prices absorb shipping, and flat percent/amount margin tweaks —
 * editable inline, creating the rule on first save.
 */
export function ClientPricingRuleGrid({ toolbar }: { toolbar: ReactNode }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();

  const clients = useMemo(() => new NexpimClientClient(http), [http]);
  const rules = useMemo(() => new ClientPricingRuleClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["pricing", "client-rules"],
    queryFn: async () => {
      const clientRows = await clients.active();
      const ruleRows = await rules.byClients(clientRows.map((row) => row.cli_id));
      const byClient = new Map(ruleRows.map((rule) => [rule.client_id, rule]));
      return clientRows.map((client): Row => {
        const rule = byClient.get(client.cli_id);
        return {
          id: client.cli_id,
          name: client.cli_name1 ?? `#${client.cli_id}`,
          no: client.cli_no ?? "",
          active: client.cli_active !== false,
          currency: client.cli_currency ?? "",
          discount: client.cli_dscnt ?? 0,
          rule_id: rule?.id ?? 0,
          has_shipping: rule?.has_shipping ?? false,
          margin_percent_adjustment: rule?.margin_percent_adjustment ?? 0,
          margin_amount_adjustment: rule?.margin_amount_adjustment ?? 0,
        };
      });
    },
    staleTime: 5 * 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<Row>();
    return [
      c.text("name", (row) => row.name, { headerKey: "column.client", priority: 1 }),
      c.text("no", (row) => row.no, { headerKey: "column.client-no", priority: 4 }),
      c.custom(
        "status",
        (row) => (row.active ? "ACTIVE" : "INACTIVE"),
        (row) => (
          <Badge variant={row.active ? "success" : "error"} size="sm">
            {row.active ? "ACTIVE" : "INACTIVE"}
          </Badge>
        ),
        { headerKey: "column.status", filter: "select" },
      ),
      c.text("currency", (row) => row.currency, {
        headerKey: "column.currency",
        filter: "select",
        priority: 2,
      }),
      c.custom(
        "discount",
        (row) => row.discount,
        (row) => <span className="tabular">{row.discount.toFixed(1)}%</span>,
        { headerKey: "column.discount" },
      ),
      c.custom(
        "has_shipping",
        (row) => (row.has_shipping ? "yes" : "no"),
        (row) =>
          row.has_shipping ? (
            <Badge variant="success" size="sm">
              {t("value.yes", "Yes")}
            </Badge>
          ) : (
            <span className="text-ink-faint">{t("value.no", "No")}</span>
          ),
        { headerKey: "pricing.has-shipping", filter: "select", priority: 3 },
      ),
      c.custom(
        "margin_percent_adjustment",
        (row) => row.margin_percent_adjustment,
        (row) => <span className="tabular">{row.margin_percent_adjustment.toFixed(1)}%</span>,
        { headerKey: "pricing.margin-percent-adjustment", priority: 3 },
      ),
      c.custom(
        "margin_amount_adjustment",
        (row) => row.margin_amount_adjustment,
        (row) => <span className="tabular">{row.margin_amount_adjustment.toFixed(2)}</span>,
        { headerKey: "pricing.margin-amount-adjustment" },
      ),
    ];
  }, [t]);

  const editor = useMemo<RowEditorConfig<Row>>(() => {
    const bounded = (value: string) => Math.max(-1000, Math.min(1000, Number(value) || 0));
    return {
      fields: {
        has_shipping: {
          kind: "checkbox",
          get: (row) => row.has_shipping,
          set: (row, value) => ({ ...row, has_shipping: value }),
        },
        margin_percent_adjustment: {
          kind: "number",
          get: (row) => row.margin_percent_adjustment,
          set: (row, value) => ({ ...row, margin_percent_adjustment: bounded(value) }),
        },
        margin_amount_adjustment: {
          kind: "number",
          get: (row) => row.margin_amount_adjustment,
          set: (row, value) => ({ ...row, margin_amount_adjustment: bounded(value) }),
        },
      },
      onSave: async (row) => {
        if (row.rule_id === 0) await rules.create(toRule(row));
        else await rules.update(toRule(row));
        await queryClient.invalidateQueries({ queryKey: ["pricing", "client-rules"] });
        toast.success(t("notification.saved", "Saved"));
      },
    };
  }, [rules, queryClient, t]);

  return (
    <>
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        toolbarExtra={toolbar}
        loading={isLoading}
        editor={editor}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
